import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getSolutionVotes, submitSolutionVote, listenSolutionVotes } from '../../../firebase/solutionVotesApi.js';

// Async thunks
export const fetchSolutionVotes = createAsyncThunk(
  'solutionView/votes/fetch',
  async ({ solutionId, userId }) => {
    return await getSolutionVotes(solutionId, userId);
  }
);

export const voteOnSolution = createAsyncThunk(
  'solutionView/votes/vote',
  async ({ solutionId, userId, voteType }) => {
    return await submitSolutionVote(solutionId, userId, voteType);
  }
);

const votingSlice = createSlice({
  name: 'solutionViewVotes',
  initialState: {
    votesBySolutionId: {}, // { [solutionId]: { yes, no, userVote, loading, error } }
    subscriptions: {} // { [solutionId]: { unsubscribe: Function, count: number } }
  },
  reducers: {
    applyVoteSnapshot(state, action) {
      const { solutionId, yes, no, userVote } = action.payload;
      state.votesBySolutionId = {
        ...state.votesBySolutionId,
        [solutionId]: { yes, no, userVote, loading: false, error: null }
      };
    },
    setVoteSubscription(state, action) {
      const { solutionId, unsubscribe } = action.payload;
      const existing = state.subscriptions[solutionId];
      if (existing) {
        state.subscriptions = {
          ...state.subscriptions,
          [solutionId]: { ...existing, count: existing.count + 1 }
        };
      } else {
        state.subscriptions = {
          ...state.subscriptions,
          [solutionId]: { unsubscribe, count: 1 }
        };
      }
    },
    decrementSubscription(state, action) {
      const { solutionId } = action.payload;
      const existing = state.subscriptions[solutionId];
      if (!existing) return;
      if (existing.count <= 1) {
        const { [solutionId]: _, ...rest } = state.subscriptions;
        state.subscriptions = rest;
      } else {
        state.subscriptions = {
          ...state.subscriptions,
          [solutionId]: { ...existing, count: existing.count - 1 }
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSolutionVotes.pending, (state, action) => {
        const id = action.meta.arg?.solutionId || action.meta.arg; // backward compatibility
        const prev = state.votesBySolutionId[id] || {};
        state.votesBySolutionId = {
          ...state.votesBySolutionId,
          [id]: { ...prev, loading: true, error: null }
        };
      })
      .addCase(fetchSolutionVotes.fulfilled, (state, action) => {
        const { solutionId, yes, no, userVote } = action.payload;
        state.votesBySolutionId = {
          ...state.votesBySolutionId,
          [solutionId]: { yes, no, userVote, loading: false, error: null }
        };
      })
      .addCase(fetchSolutionVotes.rejected, (state, action) => {
        const id = action.meta.arg?.solutionId || action.meta.arg; // backward compatibility
        const prev = state.votesBySolutionId[id] || {};
        state.votesBySolutionId = {
          ...state.votesBySolutionId,
          [id]: { ...prev, loading: false, error: action.error.message }
        };
      })
      .addCase(voteOnSolution.fulfilled, (state, action) => {
        const { solutionId, yes, no, userVote } = action.payload;
        state.votesBySolutionId = {
          ...state.votesBySolutionId,
          [solutionId]: { yes, no, userVote, loading: false, error: null }
        };
      });
  }
});

export const { applyVoteSnapshot, setVoteSubscription, decrementSubscription } = votingSlice.actions;

// Thunks for live subscriptions
export const subscribeSolutionVotes = createAsyncThunk(
  'solutionView/votes/subscribe',
  async ({ solutionId, userId }, { dispatch, getState }) => {
    if (!solutionId) return { solutionId };
    const existing = getState().solutionViewVotes?.subscriptions?.[solutionId];
    if (existing && typeof existing.unsubscribe === 'function') {
      // already subscribed, just increment
      dispatch(setVoteSubscription({ solutionId }));
    } else {
      const unsubscribe = listenSolutionVotes(solutionId, userId, (payload) => {
        dispatch(applyVoteSnapshot(payload));
      });
      dispatch(setVoteSubscription({ solutionId, unsubscribe }));
    }
    return { solutionId };
  }
);

export const unsubscribeSolutionVotes = createAsyncThunk(
  'solutionView/votes/unsubscribe',
  async ({ solutionId }, { dispatch, getState }) => {
    const existing = getState().solutionViewVotes?.subscriptions?.[solutionId];
    if (!existing) return { solutionId };
    if (existing.count <= 1) {
      if (typeof existing.unsubscribe === 'function') {
        try { existing.unsubscribe(); } catch {}
      }
    }
    dispatch(decrementSubscription({ solutionId }));
    return { solutionId };
  }
);

export default votingSlice.reducer;
